import { useEffect, useLayoutEffect, type DependencyList } from 'react';
import { useLatest } from './useLatest';

/**
 * Tell something outside React about this render's values after one of `deps` changes: post them to an
 * iframe, write them to storage, push them into a non-React store, record analytics, or scroll or focus the
 * DOM. `sync` runs after that render, with the latest values; `beforePaint` runs it before the browser paints
 * (a DOM measurement the user must not see change).
 *
 * Not for a value derived from props or state (compute it while rendering), a reset (useResetWhenChanged),
 * a read (useLoad), anything that needs cleanup (useSubscription), or telling a parent about a change (do
 * that in the handler that made the change; the decision record names the one exception, a value derived
 * from many changes the parent cannot compute).
 */
export function useSyncOutside(sync: () => void, deps: DependencyList, options: { beforePaint?: boolean } = {}): void {
    const latest = useLatest(sync);
    const beforePaint = Boolean(options.beforePaint);
    // The caller's deps decide when to sync; exhaustive-deps checks them at the call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => { if (beforePaint) latest.current(); }, deps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (!beforePaint) latest.current(); }, deps);
}
